if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLElement | null} */
        this.gallery = this.querySelector('[data-gallery]');

        /** @type {HTMLElement | null} */
        this.pagination = this.querySelector('[data-gallery-pagination]');

        /** @type {HTMLElement | null} */
        this.thumbnails = this.querySelector('[data-gallery-thumbs]');

        /** @type {HTMLElement | null} */
        this.next = this.querySelector('[data-carousel-next]');

        /** @type {HTMLElement | null} */
        this.prev = this.querySelector('[data-carousel-prev]');

        /** @type {import('swiper').Swiper | null} */
        this.instance = null;

        /** @type {import('swiper').Swiper | null} */
        this.thumbsInstance = null;

        /** @type {HTMLImageElement[]} */
        this.images = Array.from(this.querySelectorAll('img'));

        /** @type {HTMLVideoElement[]} */
        this.videos = Array.from(this.querySelectorAll('video'));

        this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
        this.handleThumbnailKeydown = this.handleThumbnailKeydown.bind(this);
      }

      connectedCallback() {
        const swiper = window.swiper.Swiper;

        if (this.thumbnails) {
          this.thumbsInstance = new swiper(this.thumbnails, {
            slidesPerView: 4,
            spaceBetween: 12,
            watchSlidesProgress: true,
            breakpoints: {
              1024: {
                slidesPerView: 'auto',
                direction: 'vertical',
              },
            },
          });

          this.thumbnails.addEventListener('click', this.handleThumbnailClick);
          this.thumbnails.addEventListener('keydown', this.handleThumbnailKeydown);
        }

        if (this.gallery) {
          this.instance = new swiper(this.gallery, {
            modules: [window.swiper.Thumbs, window.swiper.Pagination, window.swiper.Navigation],
            loop: true,
            slidesPerView: 1,
            spaceBetween: 0,
            pagination: {
              enabled: true,
              el: this.pagination,
            },
            navigation: {
              nextEl: this.next,
              prevEl: this.prev,
            },
            thumbs: {
              swiper: this.thumbsInstance,
            },
            on: {
              slideChange: (s) => {
                this.images.forEach((image) => {
                  if (image) image.setAttribute('loading', 'eager');
                });

                this.videos.forEach((video) => {
                  if (video && video.getAttribute('preload') !== 'metadata') video.setAttribute('preload', 'metadata');
                });

                const video = s.slides[s.activeIndex].querySelector('video');

                if (video) {
                  video.muted = true;
                  video.play();
                }

                if (this.thumbnails) {
                  const buttons = this.thumbnails.querySelectorAll('button');
                  buttons.forEach((button, index) => {
                    button.setAttribute('aria-selected', index === s.realIndex ? 'true' : 'false');
                  });
                }
              },
            },
          });
        }
      }

      /**
       * Handle click events on thumbnail buttons
       * @param {Event} event - The click event object
       */
      handleThumbnailClick(event) {
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest('button');
        if (!button) return;

        event.preventDefault();
        const slide = button.closest('.swiper-slide');
        if (!slide) return;

        const parent = slide.parentElement;
        if (!parent) return;

        const index = Array.from(parent.children).indexOf(slide);
        if (this.instance) {
          this.instance.slideToLoop(index);
        }
      }

      /**
       * Handle keyboard navigation for thumbnail buttons
       * @param {KeyboardEvent} event - The keyboard event object
       */
      handleThumbnailKeydown(event) {
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest('button');
        if (!button) return;

        const slide = button.closest('.swiper-slide');
        if (!slide) return;

        const parent = slide.parentElement;
        if (!parent) return;

        const slides = Array.from(parent.children);
        const currentIndex = slides.indexOf(slide);
        let targetIndex = currentIndex;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            targetIndex = Math.min(currentIndex + 1, slides.length - 1);
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            targetIndex = Math.max(currentIndex - 1, 0);
            break;
          case 'Home':
            targetIndex = 0;
            break;
          case 'End':
            targetIndex = slides.length - 1;
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (this.instance) {
              this.instance.slideToLoop(currentIndex);
            }
            return;
          default:
            return;
        }

        event.preventDefault();
        const targetButton = slides[targetIndex].querySelector('button');
        if (targetButton) {
          targetButton.focus();
          if (this.instance) {
            this.instance.slideToLoop(targetIndex);
          }
        }
      }
    },
  );
}
